import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Hammer, Home, ArrowRight, Building2, Maximize2 } from "lucide-react";
import { BRAND } from "@/lib/data";

const TILES = [
    { slug: "custom-homes", title: "Custom Homes", icon: Home },
    { slug: "renovations", title: "Renovations", icon: Hammer },
    { slug: "extensions", title: "Extensions", icon: Maximize2 },
    { slug: "knockdown-rebuilds", title: "Knockdown Rebuilds", icon: Building2 },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative bg-bone pt-24 pb-20 md:pt-32 md:pb-32 overflow-hidden"
        >
            <div className="container-wide text-center">
                {/* Brand logo */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="flex justify-center mb-6 md:mb-8"
                >
                    <img
                        src={BRAND.logoUrlFooter}
                        alt={BRAND.name}
                        className="h-20 md:h-28 w-auto object-contain"
                    />
                </motion.div>

                {/* Big condensed headline */}
                <motion.h2
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: 0.1 }}
                    className="font-condensed font-light tracking-tight text-ink text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.95] max-w-6xl mx-auto"
                >
                    Custom Homes on the Central&nbsp;Coast &amp; Newcastle
                </motion.h2>

                {/* Single CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.25 }}
                    className="mt-10 md:mt-14"
                >
                    <Link
                        to="/contact"
                        data-testid="services-cta-contact"
                        className="inline-flex items-center gap-5 bg-clay hover:bg-clayHover text-bone px-10 py-5 text-[12px] tracking-[0.28em] uppercase font-medium transition-all duration-300 hover:-translate-y-0.5"
                    >
                        Get in Touch
                        <span className="inline-block w-8 h-px bg-bone" />
                    </Link>
                </motion.div>
            </div>

            {/* Bordered service tile grid */}
            <div className="container-wide mt-28 md:mt-32">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {TILES.map((t, i) => {
                        const Icon = t.icon;
                        return (
                            <motion.a
                                key={t.slug}
                                id={t.slug}
                                href={`#${t.slug}`}
                                data-testid={`service-tile-${t.slug}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.7, delay: i * 0.1 }}
                                className="group relative border border-line bg-bone hover:bg-bone2/60 transition-all duration-500 flex flex-col items-center justify-center px-6 py-14 md:py-20 min-h-[280px] md:min-h-[340px]"
                            >
                                <Icon
                                    size={64}
                                    strokeWidth={1}
                                    className="text-clay mb-10 transition-transform duration-700 group-hover:-translate-y-1"
                                />
                                <div className="font-condensed font-light text-ink text-xl md:text-2xl tracking-wide">
                                    {t.title}
                                </div>
                                <ArrowRight
                                    size={16}
                                    strokeWidth={1.5}
                                    className="absolute bottom-6 right-6 text-ink/30 group-hover:text-clay group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                                />
                            </motion.a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
